// src/mayoreo-combos-core.ts
var normalise = (value) => value.trim().toLowerCase();
var normaliseOption = (value) => normalise(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "");
function readRules(tags) {
  const values = [...new Set(tags.map(normalise))];
  const combos = values.filter((tag) => /^combo_\d+$/.test(tag));
  const minimums = values.filter((tag) => /^minimo_\d+$/.test(tag));
  const discounts = values.filter((tag) => /^descuento_\d+(?:[.,]\d+)?$/.test(tag));
  if (combos.length !== 1 || minimums.length !== 1 || discounts.length !== 1) {
    throw new Error("No pudimos cargar las reglas de este combo. Puedes comprar por pieza con las opciones de abajo.");
  }
  const minimum = Number(minimums[0].slice(7));
  const percentage = Number(discounts[0].slice(10).replace(",", "."));
  if (!Number.isSafeInteger(minimum) || minimum < 1 || !Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
    throw new Error("Las reglas de este combo todav\xEDa no est\xE1n disponibles. Puedes comprar este producto por pieza.");
  }
  return { tag: combos[0], minimum, percentage, mandatory: values.includes("obligatorio") };
}
function sizeIndex(product, names) {
  const accepted = names.split(",").map(normaliseOption);
  return product.options.findIndex((name) => accepted.includes(normaliseOption(name)));
}
function limit(variant) {
  return Math.min(variant.maxQuantity ?? Number.MAX_SAFE_INTEGER, variant.quantityRule.max ?? Number.MAX_SAFE_INTEGER);
}
function usable(variant) {
  return variant.available && limit(variant) >= variant.quantityRule.min;
}
function validQuantity(variant, quantity) {
  return Number.isSafeInteger(quantity) && quantity >= variant.quantityRule.min && quantity <= limit(variant) && quantity % variant.quantityRule.increment === 0;
}
var ComboSelection = class {
  constructor(config, candidates) {
    this.config = config;
    this.rules = readRules(config.product.tags);
    this.products = [...new Map(candidates.filter((product) => product.tags.some((tag) => normalise(tag) === this.rules.tag)).map((product) => [product.id, product])).values()];
    if (!this.products.some((product) => product.id === config.product.id)) {
      throw new Error("No pudimos encontrar todos los productos de este combo. Puedes comprar este producto por pieza abajo.");
    }
    for (const product of this.products) {
      const rule = readRules(product.tags);
      if (rule.tag !== this.rules.tag || rule.minimum !== this.rules.minimum || rule.percentage !== this.rules.percentage) {
        throw new Error("Este combo tiene reglas distintas entre sus productos. Por ahora puedes comprar por pieza abajo.");
      }
      if (product.requiresSellingPlan || product.variantsCount > 250 || product.variantsCount !== product.variants.length) {
        throw new Error("Las opciones de este combo necesitan atenci\xF3n de la tienda. Puedes comprar este producto por pieza abajo.");
      }
      for (const variant of product.variants) {
        if (this.variants.has(variant.id) || !/^\d+$/.test(variant.id) || !Number.isSafeInteger(variant.price) || variant.price < 0 || !Number.isSafeInteger(variant.quantityRule.min) || variant.quantityRule.min < 1 || !Number.isSafeInteger(variant.quantityRule.increment) || variant.quantityRule.increment < 1 || variant.quantityRule.min % variant.quantityRule.increment !== 0) {
          throw new Error("No pudimos verificar las opciones del combo. Puedes comprar por pieza abajo.");
        }
        if (!config.allowRepeats && variant.quantityRule.min > 1) {
          throw new Error("Este combo tiene cantidades m\xEDnimas incompatibles. Puedes comprar por pieza con las opciones de abajo.");
        }
        this.variants.set(variant.id, { product, variant });
      }
    }
    const seed = this.products.find((product) => product.id === config.product.id);
    const seedVariant = seed.variants.find((variant) => variant.id === config.selectedVariantId && usable(variant)) ?? seed.variants.find(usable);
    const index = sizeIndex(seed, config.sizeNames);
    this.size = config.sameSize ? index >= 0 ? seedVariant?.options[index] ?? null : this.sizes()[0] ?? null : null;
    const initial = this.options(seed).find((variant) => variant.id === seedVariant?.id && usable(variant)) ?? this.options(seed).find(usable);
    if (initial) this.quantities.set(initial.id, initial.quantityRule.min);
  }
  config;
  products;
  rules;
  quantities = /* @__PURE__ */ new Map();
  variants = /* @__PURE__ */ new Map();
  size = null;
  sizes() {
    const sizes = [...new Set(this.products.flatMap((product) => {
      const index = sizeIndex(product, this.config.sizeNames);
      return index < 0 ? [] : product.variants.map((variant) => variant.options[index]).filter(Boolean);
    }))];
    const standard = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "XXXL", "3XL", "4XL", "5XL"];
    return sizes.sort((a, b) => {
      const first = standard.indexOf(a.toUpperCase());
      const second = standard.indexOf(b.toUpperCase());
      return first >= 0 && second >= 0 ? first - second : a.localeCompare(b, this.config.locale, { numeric: true });
    });
  }
  options(product, size = this.size) {
    const index = sizeIndex(product, this.config.sizeNames);
    return product.variants.filter((variant) => !this.config.sameSize || index < 0 || size === null || variant.options[index] === size);
  }
  isRequired(product) {
    return readRules(product.tags).mandatory;
  }
  missingRequiredProducts() {
    const selectedProducts = new Set(this.lines().map((line) => line.product.id));
    return this.products.filter((product) => this.isRequired(product) && !selectedProducts.has(product.id));
  }
  lines() {
    return [...this.quantities].flatMap(([id, quantity]) => {
      const item = this.variants.get(id);
      return item && quantity > 0 ? [{ ...item, quantity }] : [];
    });
  }
  setSize(size) {
    if (!this.sizes().includes(size)) return;
    const before = this.lines();
    this.quantities.clear();
    this.size = size;
    for (const line of before) {
      const index = sizeIndex(line.product, this.config.sizeNames);
      const replacement = this.options(line.product).find((variant) => usable(variant) && variant.options.every((value, optionIndex) => optionIndex === index || value === line.variant.options[optionIndex]));
      if (replacement && validQuantity(replacement, line.quantity)) this.quantities.set(replacement.id, line.quantity);
    }
  }
  setQuantity(id, quantity) {
    const item = this.variants.get(id);
    if (!item || !this.options(item.product).some((variant) => variant.id === id)) throw new Error("Elige una opci\xF3n disponible.");
    if (quantity === 0) {
      this.quantities.delete(id);
      return;
    }
    if (!usable(item.variant) || !validQuantity(item.variant, quantity)) throw new Error("Esa cantidad no est\xE1 disponible para esta opci\xF3n.");
    if (!this.config.allowRepeats) {
      const total = this.lines().filter((line) => line.product.id === item.product.id && line.variant.id !== id).reduce((sum, line) => sum + line.quantity, quantity);
      if (total > 1) throw new Error("Solo puedes elegir una pieza de cada producto.");
    }
    this.quantities.set(id, quantity);
  }
  totals() {
    const lines = this.lines();
    const count = lines.reduce((total, line) => total + line.quantity, 0);
    const original = lines.reduce((total, line) => total + line.quantity * line.variant.price, 0);
    const eligible = count >= this.rules.minimum && this.products.filter((product) => this.isRequired(product)).every((product) => lines.some((line) => line.product.id === product.id));
    const discount = this.config.discountReady && eligible ? this.config.nativeProxyUrl ? original - Math.round(original * (100 - this.rules.percentage) / 100) : Math.round(original * this.rules.percentage / 100) : 0;
    return { count, original, discount, total: original - discount, eligible, missing: Math.max(0, this.rules.minimum - count) };
  }
  issue() {
    if (this.config.sameSize && this.sizes().length && !this.size) return "Elige tu talla.";
    if (!this.lines().length) return "Elige al menos una pieza.";
    return null;
  }
  comboHint() {
    const totals = this.totals();
    if (!totals.count) return "Elige tu primera pieza para empezar.";
    if (totals.eligible) return "Tu combo est\xE1 completo.";
    const missingRequired = this.missingRequiredProducts();
    const unavailable = missingRequired.filter((product) => !this.options(product).some(usable));
    if (unavailable.length) {
      return `${unavailable.map((product) => product.title).join(", ")} no est\xE1 disponible para completar el combo. Puedes llevar tus piezas al precio individual.`;
    }
    const requiredUnits = missingRequired.reduce((total, product) => total + Math.min(...this.options(product).filter(usable).map((variant) => variant.quantityRule.min)), 0);
    const missing = Math.max(totals.missing, requiredUnits);
    const invitation = `Agrega ${missing} pieza${missing === 1 ? "" : "s"} m\xE1s para completar tu combo.`;
    return missingRequired.length ? `Para completar tu combo, debes agregar ${missingRequired.map((product) => `\xAB${product.title}\xBB`).join(", ")}. Te falta${missing === 1 ? "" : "n"} ${missing} pieza${missing === 1 ? "" : "s"} en total.` : invitation;
  }
  validateCart(cart) {
    if (this.issue()) throw new Error(this.issue());
    for (const line of this.lines()) {
      const existing = cart.items.filter((item) => String(item.variant_id) === line.variant.id).reduce((sum, item) => sum + item.quantity, 0);
      if (!validQuantity(line.variant, line.quantity) || existing + line.quantity > limit(line.variant)) {
        throw new Error(`No hay suficiente disponibilidad de ${line.product.title}. Revisa tambi\xE9n las piezas que ya tienes en tu carrito.`);
      }
    }
  }
  payload(attempt) {
    return { items: this.lines().map((line) => ({ id: line.variant.id, quantity: line.quantity, properties: {
      _mayoreo_intento: attempt,
      ...this.totals().eligible ? { _mayoreo_combo: this.rules.tag } : {}
    } })) };
  }
};
function attemptQuantity(cart, attempt) {
  return cart.items.filter((item) => item.properties?._mayoreo_intento === attempt).reduce((sum, item) => sum + item.quantity, 0);
}
async function submitSelection(selection, root, attempt, fetcher = fetch) {
  const request = async (path, init = {}) => {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 2e4);
    try {
      const response = await fetcher(`${root.replace(/\/$/, "")}/${path}`, { credentials: "same-origin", ...init, signal: abort.signal });
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        if (!(error instanceof SyntaxError)) throw error;
      }
      return { ok: response.ok, status: response.status, data };
    } finally {
      clearTimeout(timeout);
    }
  };
  const readCart = async () => {
    const response = await request("cart.js", { headers: { Accept: "application/json" }, cache: "no-store" });
    if (!response.ok) throw new Error("No pudimos revisar tu carrito. Int\xE9ntalo de nuevo.");
    const cart = response.data;
    if (!cart || !Array.isArray(cart.items)) throw new Error("No pudimos revisar tu carrito.");
    return cart;
  };
  selection.validateCart(await readCart());
  const expected = selection.totals().count;
  let accepted = false;
  let rejected = false;
  let description = "";
  try {
    const response = await request("cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(selection.payload(attempt)) });
    accepted = response.ok;
    rejected = [400, 401, 403, 404, 422].includes(response.status);
    const result2 = response.data;
    if (result2 && typeof result2 === "object" && "description" in result2 && typeof result2.description === "string") description = result2.description.slice(0, 400);
  } catch {
  }
  try {
    const cart = await readCart();
    const added = attemptQuantity(cart, attempt);
    const matches = selection.lines().every((line) => cart.items.filter((item) => String(item.variant_id) === line.variant.id && item.properties?._mayoreo_intento === attempt).reduce((sum, item) => sum + item.quantity, 0) === line.quantity);
    if (added === expected && matches) return { status: "complete", message: "Tu selecci\xF3n se agreg\xF3 al carrito.", added };
    if (added > 0) return { status: "partial", message: `Se agregaron ${added} de ${expected} piezas. La disponibilidad cambi\xF3; revisa tu carrito antes de continuar.`, added };
    if (accepted || !rejected) return { status: "uncertain", message: "No pudimos confirmar todas las piezas. Revisa tu carrito antes de agregar m\xE1s.", added };
    return { status: "rejected", message: description || "No pudimos agregar tu selecci\xF3n. Revisa las cantidades y la disponibilidad.", added: 0 };
  } catch {
    return { status: "uncertain", message: "No pudimos confirmar si se agreg\xF3 tu selecci\xF3n. Revisa tu carrito antes de intentar de nuevo.", added: 0 };
  }
}

// src/mayoreo-native-cart.ts
var GID = "gid://shopify/ProductVariant/";
var TIMEOUT_MS = 45e3;
var MESSAGES = {
  complete: "Tu selecci\xF3n se agreg\xF3 al carrito.",
  rejected: "No pudimos agregar tu selecci\xF3n. Revisa las opciones, la disponibilidad y tu carrito antes de intentar de nuevo.",
  partial: "Se agreg\xF3 parte de la selecci\xF3n, pero no pudimos confirmar el resultado. Revisa tu carrito antes de pagar.",
  uncertain: "No pudimos confirmar c\xF3mo qued\xF3 tu selecci\xF3n. Revisa tu carrito antes de agregar m\xE1s o pagar."
};
var result = (status, added = 0) => ({
  status,
  message: MESSAGES[status],
  added
});
var record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
var invalid = () => {
  throw new Error("INVALID_NATIVE_CART_DATA");
};
function digits(value) {
  const text = typeof value === "number" && Number.isSafeInteger(value) ? String(value) : value;
  if (typeof text !== "string" || text.length > 80 || !/^[1-9]\d*$/.test(text)) return invalid();
  return text;
}
function numericGid(value) {
  if (typeof value !== "string" || !value.startsWith(GID)) return invalid();
  return digits(value.slice(GID.length));
}
function localPath(value) {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) return invalid();
  const parsed = new URL(value, "https://mayoreo.invalid");
  if (parsed.origin !== "https://mayoreo.invalid" || parsed.search || parsed.hash || parsed.username || parsed.password) return invalid();
  return parsed.pathname;
}
function properties(value) {
  if (value === void 0 || value === null) return {};
  if (!record(value)) return invalid();
  const entries = Object.entries(value);
  if (entries.some(([, item]) => typeof item !== "string")) return invalid();
  return Object.fromEntries(entries);
}
function parseCart(value) {
  if (!record(value) || !Array.isArray(value.items) || value.items.length > 250) return invalid();
  const keys = /* @__PURE__ */ new Set();
  const items = value.items.map((item) => {
    if (!record(item)) return invalid();
    const id = digits(item.variant_id);
    if (typeof item.key !== "string" || item.key.length > 256 || !/^[1-9]\d*:[A-Za-z0-9_-]+$/.test(item.key) || !item.key.startsWith(`${id}:`) || keys.has(item.key) || typeof item.quantity !== "number" || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return invalid();
    keys.add(item.key);
    return {
      key: item.key,
      variant_id: id,
      quantity: item.quantity,
      properties: properties(item.properties),
      hasSellingPlan: item.selling_plan_allocation !== void 0 && item.selling_plan_allocation !== null
    };
  });
  return { items };
}
function fingerprint(items) {
  return JSON.stringify([...items].sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0).map((item) => [
    item.key,
    item.variant_id,
    item.quantity,
    item.hasSellingPlan,
    Object.entries(item.properties).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)
  ]));
}
function parseReply(value, cart, selectedCount) {
  if (!record(value)) return invalid();
  if (value.kind === "pieces") {
    if (Object.keys(value).length !== 1) return invalid();
    return { kind: "pieces" };
  }
  const allowed = ["kind", "parentVariantId", "replaceVariantIds", "componentCount", "priceMinor", "subtotalMinor"];
  if (value.kind !== "bundle" || Object.keys(value).length !== allowed.length || Object.keys(value).some((key) => !allowed.includes(key))) return invalid();
  numericGid(value.parentVariantId);
  if (!Array.isArray(value.replaceVariantIds) || value.replaceVariantIds.length > 250) return invalid();
  const known = new Set(cart.items.map((item) => `${GID}${item.variant_id}`));
  if (known.has(value.parentVariantId)) return invalid();
  const replacement = /* @__PURE__ */ new Set();
  const replaceVariantIds = value.replaceVariantIds.map((id) => {
    numericGid(id);
    if (typeof id !== "string" || replacement.has(id) || !known.has(id)) return invalid();
    replacement.add(id);
    return id;
  });
  const oldUnits = cart.items.filter((line) => replacement.has(`${GID}${line.variant_id}`)).reduce((sum, line) => sum + line.quantity, 0);
  if (typeof value.componentCount !== "number" || !Number.isSafeInteger(value.componentCount) || value.componentCount < selectedCount + oldUnits || value.componentCount > 200 || typeof value.priceMinor !== "number" || !Number.isSafeInteger(value.priceMinor) || value.priceMinor < 0 || typeof value.subtotalMinor !== "number" || !Number.isSafeInteger(value.subtotalMinor) || value.subtotalMinor < value.priceMinor) return invalid();
  return {
    kind: "bundle",
    parentVariantId: value.parentVariantId,
    replaceVariantIds,
    componentCount: value.componentCount,
    priceMinor: value.priceMinor,
    subtotalMinor: value.subtotalMinor
  };
}
function selectedLines(selection) {
  const seen = /* @__PURE__ */ new Set();
  const lines = selection.lines().map((line) => {
    const id = digits(line.variant.id);
    if (seen.has(id) || !Number.isSafeInteger(line.quantity) || line.quantity < 1 || line.quantity > 200) return invalid();
    seen.add(id);
    return { id: `${GID}${id}`, quantity: line.quantity };
  });
  if (!lines.length || lines.length > 30 || lines.reduce((sum, line) => sum + line.quantity, 0) > 200) return invalid();
  return lines;
}
function confirmedParent(cart, id) {
  const marked = cart.items.filter((line2) => line2.variant_id === id);
  const line = marked.length === 1 ? marked[0] : void 0;
  return line && line.quantity === 1 && !line.hasSellingPlan ? line : void 0;
}
async function submitNativeSelection(selection, root, proxyUrl, attempt, fetcher = fetch) {
  let base;
  let proxy;
  let requested;
  let before;
  let quote;
  const request = async (url, init = {}) => {
    const controller = new AbortController();
    let timeout;
    const expired = new Promise((_, reject) => {
      timeout = setTimeout(() => {
        controller.abort();
        reject(new Error("NATIVE_REQUEST_TIMEOUT"));
      }, TIMEOUT_MS);
    });
    const perform = async () => {
      const response = await fetcher(url, { credentials: "same-origin", ...init, signal: controller.signal });
      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        if (!(error instanceof SyntaxError)) throw error;
      }
      return { ok: response.ok, status: response.status, data };
    };
    try {
      return await Promise.race([perform(), expired]);
    } finally {
      if (timeout !== void 0) clearTimeout(timeout);
    }
  };
  const readCart = async () => {
    const response = await request(`${base}/cart.js`, { headers: { Accept: "application/json" }, cache: "no-store" });
    if (!response.ok) return invalid();
    return parseCart(response.data);
  };
  try {
    base = localPath(root).replace(/\/$/, "");
    proxy = localPath(proxyUrl);
    if (typeof attempt !== "string" || !/^[A-Za-z0-9_-]{1,160}$/.test(attempt) || !/^combo_\d+$/.test(selection.rules.tag)) return result("rejected");
    requested = selectedLines(selection);
    before = await readCart();
    if (before.items.some((line) => line.properties._mayoreo_intento === attempt)) return result("uncertain");
    selection.validateCart(before);
    const prepared = await request(proxy, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ selection: requested, cartLines: before.items.map((line) => ({ id: `${GID}${line.variant_id}`, quantity: line.quantity })) })
    });
    if (!prepared.ok) return result("rejected");
    quote = parseReply(prepared.data, before, requested.reduce((sum, line) => sum + line.quantity, 0));
    if (JSON.stringify(selectedLines(selection)) !== JSON.stringify(requested)) return result("rejected");
  } catch {
    return result("rejected");
  }
  if (quote.kind === "pieces") {
    let previousAttempt = false;
    let reads = 0;
    const guardedFetcher = async (input, init) => {
      const response = await fetcher(input, init);
      if (input === `${base}/cart.js` && response.ok) {
        const snapshot = parseCart(await response.json());
        reads++;
        if (reads === 1 && snapshot.items.some((line) => line.properties._mayoreo_intento === attempt)) {
          previousAttempt = true;
          return invalid();
        }
        return new Response(JSON.stringify(snapshot), {
          status: response.status,
          headers: { "Content-Type": "application/json" }
        });
      }
      return response;
    };
    try {
      const submitted = await submitSelection(selection, root, attempt, guardedFetcher);
      return result(submitted.status, submitted.added);
    } catch {
      return result(previousAttempt ? "uncertain" : "rejected");
    }
  }
  const parentId = numericGid(quote.parentVariantId);
  const replaceIds = new Set(quote.replaceVariantIds.map(numericGid));
  const oldLines = before.items.filter((line) => replaceIds.has(line.variant_id));
  if (oldLines.some((line) => line.hasSellingPlan || Object.keys(line.properties).some((key) => key !== "_mayoreo_intento" && key !== "_mayoreo_combo"))) return result("rejected");
  try {
    const fresh = await readCart();
    if (fresh.items.some((line) => line.variant_id === parentId) || fingerprint(fresh.items) !== fingerprint(before.items)) return result("rejected");
  } catch {
    return result("rejected");
  }
  const oldKeys = new Set(oldLines.map((line) => line.key));
  const updates = Object.fromEntries([...oldLines.map((line) => [line.key, 0]), [parentId, 1]]);
  let knownRejected = false;
  try {
    const updated = await request(`${base}/cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates })
    });
    knownRejected = [400, 401, 403, 404, 422].includes(updated.status);
  } catch {
  }
  try {
    const final = await readCart();
    const finalParent = confirmedParent(final, parentId);
    if (finalParent && !final.items.some((line) => oldKeys.has(line.key)) && fingerprint(final.items.filter((line) => line.key !== finalParent.key)) === fingerprint(before.items.filter((line) => !oldKeys.has(line.key)))) return result("complete", quote.componentCount);
    if (knownRejected && fingerprint(final.items) === fingerprint(before.items)) return result("rejected");
    return {
      status: "uncertain",
      added: finalParent ? quote.componentCount : 0,
      message: "No pudimos confirmar el reemplazo de las piezas. Revisa las piezas y cantidades de tu carrito antes de agregar m\xE1s o pagar."
    };
  } catch {
    return result("uncertain");
  }
}

// src/mayoreo-measurements.ts
var MAX_BYTES = 64 * 1024;
var ALLOWED = /* @__PURE__ */ new Set(["table", "thead", "tbody", "tfoot", "tr", "th", "td", "p", "br", "strong", "em", "ul", "ol", "li"]);
var DROP = "script,style,svg,iframe,img,form,object,template,embed,math,canvas,video,audio,picture,source,input,textarea,select,button,link,meta,base,noscript";
var BLOCK = "p,ul,ol,h1,h2,h3,h4,h5,h6";
var SIZE = /\b(?:tallas?|sizes?|tamanos?)\b/;
var MEASURE = /\b(?:anchos?|anchura|largos?|longitud|pecho|busto|cintura|caderas?|hombros?|mangas?|entrepierna|contorno|altos?|altura|width|length|chest|bust|waist|hips?|shoulders?|sleeves?|inseam|height)\b/;
var MEASURE_HEADING = /\b(?:medidas?|measurements?|dimensiones|dimensions)\b/;
var UNIT = /\b(?:cm|mm|centimetros?|centimeters?|inches?|pulgadas?)\b/;
var FINANCE = /\b(?:precio|price|descuento|discount|oferta|sale|ahorra|save|envio|shipping)\b|[%$€]/;
var normalise2 = (value) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
var escape = (value) => value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);
var hasNumber = (value) => /\d/.test(value.replace(/\b\d{1,2}x{0,3}l\b/g, ""));
var hasMeasure = (value) => (MEASURE.test(value) || MEASURE_HEADING.test(value)) && hasNumber(value) && (!FINANCE.test(value) || UNIT.test(value));
function cellText(element) {
  const copy = element.cloneNode(true);
  copy.querySelectorAll("table").forEach((nested) => nested.remove());
  return normalise2(copy.textContent ?? "");
}
function safe(node) {
  if (node.nodeType === 3) return escape(node.textContent ?? "");
  if (node.nodeType !== 1) return "";
  const element = node;
  const original = element.tagName.toLowerCase();
  if (element.matches(DROP)) return "";
  const tag = original === "b" ? "strong" : original === "i" ? "em" : /^h[1-6]$/.test(original) ? "p" : original;
  const content = [...element.childNodes].map(safe).join("");
  if (!ALLOWED.has(tag)) return content;
  return tag === "br" ? "<br>" : `<${tag}>${content}</${tag}>`;
}
function blocks(parent) {
  const found = [];
  let inline = [];
  const flush = () => {
    const text = normalise2(inline.map((node) => node.textContent ?? "").join(" "));
    if (text) found.push({ element: null, text, html: `<p>${inline.map(safe).join("")}</p>` });
    inline = [];
  };
  for (const node of parent.childNodes) {
    if (node.nodeType !== 1) {
      inline.push(node);
      continue;
    }
    const element = node;
    if (element.tagName.toLowerCase() === "table") {
      flush();
      continue;
    }
    if (element.matches(BLOCK)) {
      flush();
      found.push({ element, text: normalise2(element.textContent ?? ""), html: safe(element) });
    } else if (element.querySelector(`${BLOCK},table`) || /^(?:div|section|article|main|blockquote)$/.test(element.tagName.toLowerCase())) {
      flush();
      found.push(...blocks(element));
    } else inline.push(node);
  }
  flush();
  return found;
}
function sizeContext(block) {
  if (!SIZE.test(block.text)) return null;
  if (block.element?.matches("ul,ol")) {
    const items = [...block.element.children].filter((element) => element.tagName.toLowerCase() === "li" && SIZE.test(normalise2(element.textContent ?? "")) && !FINANCE.test(normalise2(element.textContent ?? "")));
    return items.length ? `<${block.element.tagName.toLowerCase()}>${items.map(safe).join("")}</${block.element.tagName.toLowerCase()}>` : null;
  }
  return !FINANCE.test(block.text) && /^(?:tallas?|sizes?|tamanos?)\b/.test(block.text) ? block.html : null;
}
function sanitizeMeasurements(html) {
  if (typeof html !== "string" || !html.trim() || html.length > MAX_BYTES || new TextEncoder().encode(html).length > MAX_BYTES) return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll(DROP).forEach((element) => element.remove());
  for (const table of doc.body.querySelectorAll("table")) {
    if ([...table.querySelectorAll("[rowspan],[colspan]")].some((cell) => cell.closest("table") === table && (Number(cell.getAttribute("rowspan") ?? 1) !== 1 || Number(cell.getAttribute("colspan") ?? 1) !== 1))) continue;
    const rows = [...table.querySelectorAll("tr")].filter((row) => row.closest("table") === table);
    if (rows.length < 2) continue;
    const headings = [...table.querySelectorAll("th,thead td"), ...rows[0].children].filter((cell) => cell.closest("table") === table).map(cellText).join(" ");
    const numericData = rows.slice(1).some((row) => [...row.children].some((cell) => hasNumber(cellText(cell))));
    if (!SIZE.test(headings) || !MEASURE.test(headings) || !numericData) continue;
    const preceding = table.previousElementSibling;
    const intro = preceding?.tagName.toLowerCase() === "p" && (MEASURE_HEADING.test(normalise2(preceding.textContent ?? "")) || UNIT.test(normalise2(preceding.textContent ?? ""))) ? safe(preceding) : "";
    const caption = [...table.children].find((child) => child.tagName.toLowerCase() === "caption");
    const captionHtml = caption ? `<p>${[...caption.childNodes].map(safe).join("")}</p>` : "";
    caption?.remove();
    table.querySelectorAll("table").forEach((nested) => nested.remove());
    return `${intro}${captionHtml}${safe(table)}`;
  }
  doc.body.querySelectorAll("table").forEach((table) => table.remove());
  const candidates = blocks(doc.body);
  const output = /* @__PURE__ */ new Map();
  for (let index = 0; index < candidates.length; index++) {
    const block = candidates[index];
    let content = null;
    if (block.element?.matches("ul,ol")) {
      const items = [...block.element.children].filter((item) => item.tagName.toLowerCase() === "li" && hasMeasure(normalise2(item.textContent ?? "")));
      if (items.length) content = `<${block.element.tagName.toLowerCase()}>${items.map(safe).join("")}</${block.element.tagName.toLowerCase()}>`;
    } else if (hasMeasure(block.text)) content = block.html;
    if (!content) continue;
    output.set(index, content);
    const previous = candidates[index - 1];
    if (previous && (MEASURE_HEADING.test(previous.text) || UNIT.test(previous.text)) && !FINANCE.test(previous.text)) output.set(index - 1, previous.html);
    for (let before = index - 1; before >= Math.max(0, index - 3); before--) {
      const context = sizeContext(candidates[before]);
      if (context) {
        if (!output.has(before)) output.set(before, context);
        break;
      }
    }
  }
  return output.size ? [...output].sort(([first], [second]) => first - second).map(([, content]) => content).join("") : null;
}

// src/mayoreo-combos.ts
var escape2 = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);
var loadingMessage = (error) => error instanceof Error && /^(No pudimos|Las reglas|Este combo|Las opciones)/.test(error.message) ? error.message : "No pudimos cargar tu combo. Puedes comprar este producto por pieza con las opciones de abajo.";
function image(url, title, className) {
  if (!url || !/^(https?:\/\/|\/)/.test(url)) return `<div class="${className} mc-card-placeholder" aria-hidden="true">Sin imagen</div>`;
  return `<img class="${className}" src="${escape2(url)}" alt="${escape2(title)}" loading="lazy" width="600" height="600">`;
}
async function loadCatalog(config, signal) {
  const rules = readRules(config.product.tags);
  const url = new URL(config.searchUrl, window.location.origin);
  url.searchParams.set("q", `tag:"${rules.tag}"`);
  url.searchParams.set("type", "product");
  url.searchParams.set("options[unavailable_products]", "show");
  url.searchParams.set("options[prefix]", "none");
  url.searchParams.set("section_id", "mayoreo-combo-data");
  const products = [];
  const visited = /* @__PURE__ */ new Set();
  let expectedCount = null;
  for (let pageNumber = 1; pageNumber <= 20; pageNumber++) {
    url.searchParams.set("page", String(pageNumber));
    const response = await fetch(url, { signal, credentials: "same-origin", headers: { Accept: "text/html" } });
    if (!response.ok) throw new Error("No pudimos cargar las opciones. Puedes comprar este producto por pieza abajo.");
    const doc = new DOMParser().parseFromString(await response.text(), "text/html");
    const raw = doc.querySelector("[data-combo-page]")?.textContent;
    if (!raw) throw new Error("No pudimos cargar el combo completo. Puedes comprar este producto por pieza abajo.");
    const page = JSON.parse(raw);
    if (!Array.isArray(page.products) || !Number.isSafeInteger(page.page) || page.page !== pageNumber || visited.has(page.page) || !Number.isSafeInteger(page.resultsCount) || page.resultsCount < 0 || page.resultsCount >= 1e3 || expectedCount !== null && page.resultsCount !== expectedCount) {
      throw new Error("Este combo necesita atenci\xF3n de la tienda. Puedes comprar este producto por pieza abajo.");
    }
    expectedCount = page.resultsCount;
    visited.add(page.page);
    products.push(...page.products);
    if (!page.nextPage) {
      if (new Set(products.map((product) => product.id)).size !== expectedCount) throw new Error("No pudimos cargar el combo completo. Puedes comprar por pieza abajo.");
      return products;
    }
    const next = new URL(page.nextPage, window.location.origin);
    if (next.origin !== window.location.origin || next.pathname !== url.pathname || Number(next.searchParams.get("page")) !== pageNumber + 1) {
      throw new Error("No pudimos cargar el combo completo. Puedes comprar por pieza abajo.");
    }
  }
  throw new Error("Este combo tiene demasiadas opciones. Puedes comprar por pieza abajo.");
}
var MayoreoComboBuilder = class extends HTMLElement {
  config;
  selection;
  app;
  original = null;
  controller = null;
  choices = /* @__PURE__ */ new Map();
  measurementProduct = "";
  measurementOpen = false;
  measurementOpener = "";
  measurementContent = /* @__PURE__ */ new Map();
  message;
  busy = false;
  locked = false;
  initialised = false;
  connectedCallback() {
    if (!this.initialised) {
      this.initialised = true;
      void this.start();
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
  }
  async start() {
    this.app = this.querySelector("[data-combo-app]");
    const loading = this.querySelector("[data-combo-loading]");
    this.message = document.createElement("p");
    this.message.className = "mc-sr-only";
    this.message.setAttribute("role", "status");
    this.message.setAttribute("aria-live", "polite");
    this.append(this.message);
    try {
      this.config = JSON.parse(this.querySelector("[data-combo-config]").textContent);
      for (const path of [this.config.root, this.config.searchUrl, this.config.cartUrl]) {
        if (new URL(path, window.location.origin).origin !== window.location.origin) throw new Error("No pudimos abrir este combo.");
      }
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller?.abort(), 25e3);
      let catalog;
      try {
        catalog = await loadCatalog(this.config, this.controller.signal);
      } finally {
        clearTimeout(timeout);
      }
      if (!this.isConnected) return;
      this.config.selectedVariantId = new URL(window.location.href).searchParams.get("variant") ?? void 0;
      this.selection = new ComboSelection(this.config, catalog);
      this.measurementProduct = this.config.product.id;
      this.original = this.closest(".shopify-section")?.querySelector("[data-combo-original]") ?? null;
      this.addEventListener("click", (event) => this.onClick(event));
      this.addEventListener("change", (event) => this.onChange(event));
      this.render();
      loading.hidden = true;
      if (this.original) this.original.hidden = true;
    } catch (error) {
      if (!this.isConnected) return;
      loading.hidden = true;
      this.message.className = "mc-error";
      this.message.textContent = this.config?.loadError ?? loadingMessage(error);
    }
  }
  money(value) {
    const locale = this.config.currency === "GTQ" && this.config.locale === "es" ? "es-GT" : this.config.locale || "es";
    return new Intl.NumberFormat(locale, { style: "currency", currency: this.config.currency }).format(value / 100);
  }
  announce(text, error = false) {
    this.message.className = "mc-sr-only";
    this.message.textContent = text;
    const visible = this.app.querySelector("[data-combo-visible-message]");
    if (visible) {
      visible.className = error ? "mc-error" : "mc-status";
      visible.textContent = text;
    }
  }
  chosen(product) {
    const options = this.selection.options(product);
    const chosen = options.find((variant) => variant.id === this.choices.get(product.id) && usable(variant)) ?? options.find(usable) ?? options[0];
    if (chosen) this.choices.set(product.id, chosen.id);
    return chosen;
  }
  render(focus = "") {
    const state = this.selection;
    const totals = state.totals();
    const issue = state.issue();
    const sizes = this.config.sameSize ? state.sizes() : [];
    const visibleProducts = state.products.filter((product) => state.options(product).some(usable));
    const disabled = this.busy || this.locked ? "disabled" : "";
    const subtitle = `Elige tus piezas. Puedes llevar solo una o completar tu combo${this.config.discountReady ? ` con ${state.rules.percentage}% de descuento` : ""}.`;
    this.app.innerHTML = `<div class="mc-shell" aria-busy="${this.busy}">
      <div class="mc-main"><h1 class="mc-heading">Arma tu <span>combo</span></h1>
      <p class="mc-subtitle">${escape2(subtitle)}</p>
      ${sizes.length ? `<section class="mc-step" aria-label="Elige tu talla"><h2 class="mc-step-title">1. Elige tu talla</h2>
        <div class="mc-sizes" role="group" aria-label="Talla para todas las piezas">${sizes.map((size, index) => `<button type="button" data-action="size" data-size="${escape2(size)}" data-focus="size-${index}" aria-pressed="${state.size === size}" ${disabled}>${escape2(size)}</button>`).join("")}</div>
        <p class="mc-help">La misma talla para todas tus piezas.</p></section>` : ""}
      <section class="mc-step" aria-label="Elige tus productos"><h2 class="mc-step-title">${sizes.length ? "2." : "1."} Elige tus productos</h2>
        <p class="mc-help">${state.size ? `Solo se muestran productos disponibles en talla ${escape2(state.size)}.` : "Selecciona la opci\xF3n de cada producto."}${state.products.some((product) => state.isRequired(product)) ? " Los productos marcados son necesarios para completar el combo y cuentan para el m\xEDnimo." : ""}</p>
        ${visibleProducts.length ? `<div class="mc-grid">${visibleProducts.map((product) => this.card(product, disabled)).join("")}</div>` : '<p class="mc-notice">No hay productos disponibles en esta talla. Elige otra talla para ver las opciones.</p>'}
      </section></div>
      <aside class="mc-summary" aria-label="Resumen de tu selecci\xF3n"><h2 class="mc-summary-heading">${totals.eligible ? "Tu combo" : "Tu selecci\xF3n"}</h2>
        <p class="mc-summary-meta">${totals.count} pieza${totals.count === 1 ? "" : "s"}${state.size ? ` \xB7 Talla ${escape2(state.size)}` : ""}</p>
        <div class="mc-lines">${state.lines().length ? state.lines().map((line) => {
      const required = state.isRequired(line.product);
      const productCount = state.lines().filter((item) => item.product.id === line.product.id).reduce((sum, item) => sum + item.quantity, 0);
      const step = line.variant.quantityRule.increment;
      const plusDisabled = line.quantity + step > limit(line.variant) || !this.config.allowRepeats && productCount >= 1;
      return `<div class="mc-line">${image(line.variant.image ?? line.product.image, line.product.title, "mc-line-image")}
            <div class="mc-line-info"><p class="mc-line-title">${escape2(line.product.title)}</p><p class="mc-line-meta">${escape2(line.variant.title === "Default Title" ? "" : line.variant.title)} \xB7 ${this.money(line.variant.price)}</p>${required ? '<span class="mc-required-label">Necesario para el combo</span>' : ""}</div>
            <div class="mc-quantity"><button type="button" data-action="minus" data-variant="${line.variant.id}" data-focus="minus-${line.variant.id}" aria-label="Quitar una pieza de ${escape2(line.product.title)}" ${disabled}>\u2212</button>
              <input type="number" value="${line.quantity}" min="${line.variant.quantityRule.min}" step="${step}" ${limit(line.variant) < Number.MAX_SAFE_INTEGER ? `max="${limit(line.variant)}"` : ""} inputmode="numeric" data-quantity="${line.variant.id}" data-focus="quantity-${line.variant.id}" aria-label="Cantidad de ${escape2(line.product.title)}" ${disabled}>
              <button type="button" data-action="plus" data-variant="${line.variant.id}" data-focus="plus-${line.variant.id}" aria-label="Agregar una pieza de ${escape2(line.product.title)}" ${disabled || (plusDisabled ? "disabled" : "")}>+</button></div>
            <button type="button" class="mc-remove" data-action="remove" data-variant="${line.variant.id}" data-focus="remove-${line.variant.id}" aria-label="Eliminar ${escape2(line.product.title)}" ${disabled}>\xD7</button>
          </div>`;
    }).join("") : '<p class="mc-empty">Aqu\xED ver\xE1s las piezas que elijas.</p>'}</div>
        <div class="mc-summary-footer">
          <div class="mc-price-row mc-total"><span>${totals.discount > 0 ? "Total estimado" : "Subtotal"}</span><strong>${this.money(totals.total)}</strong></div>
          ${totals.discount > 0 ? `<p class="mc-savings">Ahorro estimado ${this.money(totals.discount)}</p>` : ""}
          <progress class="mc-progress" value="${Math.min(totals.count, state.rules.minimum)}" max="${state.rules.minimum}" aria-label="Cantidad m\xEDnima del combo">${totals.count} de ${state.rules.minimum}</progress>
          <p class="${issue || !totals.eligible ? "mc-notice" : "mc-help"}">${escape2(issue ?? state.comboHint())}</p>
          <p data-combo-visible-message class="mc-status" aria-hidden="true">${escape2(this.message?.textContent)}</p>
          <button type="button" class="mc-submit" data-action="submit" data-focus="submit" aria-busy="${this.busy}" ${disabled || (issue ? "disabled" : "")}>${this.busy ? "Agregando\u2026" : "Agregar al carrito"}</button>
          <a class="mc-cart-link" href="${escape2(this.config.cartUrl)}">Ver carrito</a>
          ${this.config.discountReady ? '<p class="mc-help">El total y las promociones se confirman en el carrito.</p>' : ""}
        </div></aside></div>${this.measurements(disabled)}`;
    const measuredProduct = state.products.find((product) => product.id === this.measurementProduct);
    const measuredSizeIndex = measuredProduct ? sizeIndex(measuredProduct, this.config.sizeNames) : -1;
    const measuredSize = state.size ?? (measuredProduct && measuredSizeIndex >= 0 ? this.chosen(measuredProduct)?.options[measuredSizeIndex] : null);
    if (measuredSize) {
      for (const row of this.app.querySelectorAll(".mc-measurement-content tbody tr")) {
        if (Array.from(row.cells).some((cell) => cell.textContent?.trim().toLocaleLowerCase() === measuredSize.toLocaleLowerCase())) row.classList.add("is-current");
      }
    }
    const dialog = this.app.querySelector("[data-measurement-dialog]");
    if (dialog) {
      dialog.showModal();
      dialog.addEventListener("cancel", (event) => {
        event.preventDefault();
        this.closeMeasurements();
      }, { once: true });
    }
    if (focus) this.app.querySelector(`[data-focus="${focus}"]`)?.focus({ preventScroll: true });
  }
  closeMeasurements() {
    this.measurementOpen = false;
    this.render(this.measurementOpener);
  }
  measurements(disabled) {
    const labels = this.config.measurementLabels;
    if (!labels || !this.measurementOpen) return "";
    const product = this.selection.products.find((item) => item.id === this.measurementProduct);
    if (!product) return "";
    if (!this.measurementContent.has(product.id)) this.measurementContent.set(product.id, sanitizeMeasurements(product.descriptionHtml));
    const content = this.measurementContent.get(product.id);
    const titleId = `mc-measurement-${this.dataset.sectionId ?? "main"}-${product.id}`;
    const productSizeIndex = sizeIndex(product, this.config.sizeNames);
    const sizes = this.config.sameSize && productSizeIndex >= 0 ? this.selection.sizes() : [];
    return `<dialog class="mc-measurement-dialog" data-measurement-dialog aria-labelledby="${escape2(titleId)}">
      <div class="mc-dialog-heading"><h2 id="${escape2(titleId)}">${escape2(labels.title)}</h2><button type="button" class="mc-dialog-close" data-action="close-measurements" data-focus="measurement-close" aria-label="${escape2(labels.close ?? labels.title)}">\xD7</button></div>
      <div class="mc-dialog-product">${image(product.image, product.title, "mc-dialog-image")}<h3>${escape2(product.title)}</h3></div>
      ${sizes.length ? `<p class="mc-help">${escape2(labels.chooseSize ?? labels.title)}</p><div class="mc-sizes" role="group" aria-label="${escape2(labels.chooseSize ?? labels.title)}">${sizes.map((size, index) => {
      const available = product.variants.some((variant) => usable(variant) && variant.options[productSizeIndex] === size);
      return `<button type="button" data-action="size" data-size="${escape2(size)}" data-focus="measurement-size-${index}" aria-pressed="${this.selection.size === size}" ${disabled || (!available ? "disabled" : "")}>${escape2(size)}</button>`;
    }).join("")}</div>` : ""}
      <div class="mc-measurement-body">
        <div class="mc-measurement-content" role="region" tabindex="0" aria-label="${escape2(labels.title)}: ${escape2(product.title)}">${content ?? `<p>${escape2(labels.empty)}</p>`}</div>
        <p class="mc-help">${escape2(labels.help)}</p>
      </div></dialog>`;
  }
  card(product, disabled) {
    const variant = this.chosen(product);
    const options = this.selection.options(product);
    const available = variant && usable(variant);
    const productCount = this.selection.lines().filter((line) => line.product.id === product.id).reduce((sum, line) => sum + line.quantity, 0);
    const quantity = variant ? this.selection.quantities.get(variant.id) ?? 0 : 0;
    const canAdd = available && (quantity === 0 || quantity + variant.quantityRule.increment <= limit(variant)) && (this.config.allowRepeats || productCount === 0);
    const required = this.selection.isRequired(product);
    return `<article class="mc-card" data-selected="${productCount > 0}" data-unavailable="${!available}">
      ${this.config.measurementLabels ? `<button type="button" class="mc-card-preview" data-action="measurements" data-product="${product.id}" data-focus="measure-image-${product.id}" aria-label="${escape2(this.config.measurementLabels.open ?? this.config.measurementLabels.title)}: ${escape2(product.title)}" ${disabled}>${image(variant?.image ?? product.image, product.title, "mc-card-image")}</button>` : image(variant?.image ?? product.image, product.title, "mc-card-image")}
      ${required ? '<span class="mc-card-badge">Para el combo</span>' : productCount > 0 ? '<span class="mc-card-badge" aria-label="Seleccionado">\u2713</span>' : ""}
      <div class="mc-card-body"><h3 class="mc-card-title">${escape2(product.title)}</h3>
      <p class="mc-card-price">${variant ? this.money(variant.price) : "Sin disponibilidad"}</p>
      ${this.config.measurementLabels ? `<button type="button" class="mc-card-measurements" data-action="measurements" data-product="${product.id}" data-focus="measure-link-${product.id}" ${disabled}>${escape2(this.config.measurementLabels.open ?? this.config.measurementLabels.title)}</button>` : ""}
      ${options.length > 1 ? `<select class="mc-variant-select" data-choice="${product.id}" data-focus="choice-${product.id}" aria-label="Opci\xF3n de ${escape2(product.title)}" ${disabled}>${options.map((option) => `<option value="${option.id}" ${variant?.id === option.id ? "selected" : ""} ${!usable(option) ? "disabled" : ""}>${escape2(option.title)}${!usable(option) ? " \xB7 Agotado" : ""}</option>`).join("")}</select>` : ""}
      ${!available ? '<span class="mc-help">Agotado en esta opci\xF3n</span>' : ""}
      <button type="button" class="mc-card-add" data-action="add" data-variant="${variant?.id ?? ""}" data-focus="add-${product.id}" aria-label="Agregar ${escape2(product.title)}" ${disabled || (!canAdd ? "disabled" : "")}>+</button>
      </div></article>`;
  }
  onClick(event) {
    const target = event.target instanceof Element ? event.target.closest("button[data-action]") : null;
    if (!target || !this.contains(target) || target.disabled || this.busy || this.locked) return;
    const action = target.dataset.action;
    const id = target.dataset.variant ?? "";
    if (action === "measurements") {
      if (!this.selection.products.some((product) => product.id === target.dataset.product)) return;
      this.measurementProduct = target.dataset.product;
      this.measurementOpen = true;
      this.measurementOpener = target.dataset.focus ?? "";
      this.render("measurement-close");
      return;
    }
    if (action === "close-measurements") {
      this.closeMeasurements();
      return;
    }
    if (action === "submit") {
      void this.submit();
      return;
    }
    try {
      if (action === "size") {
        this.selection.setSize(target.dataset.size);
        this.choices.clear();
        this.announce(`Talla ${this.selection.size}. Revisa tu selecci\xF3n; solo conservamos las piezas disponibles en esta talla.`);
      } else {
        const variant = this.selection.variants.get(id)?.variant;
        if (!variant) return;
        const previous = this.selection.quantities.get(id) ?? 0;
        const reduced = previous - variant.quantityRule.increment;
        const next = action === "remove" ? 0 : action === "minus" ? reduced < variant.quantityRule.min ? 0 : reduced : previous ? previous + variant.quantityRule.increment : variant.quantityRule.min;
        this.selection.setQuantity(id, next);
        this.announce(`${this.selection.totals().count} piezas en tu selecci\xF3n.`);
      }
    } catch (error) {
      this.announce(error instanceof Error ? error.message : "Revisa tu selecci\xF3n.", true);
    }
    this.render(target.dataset.focus);
  }
  onChange(event) {
    const target = event.target;
    if (this.busy || this.locked) return;
    try {
      if (target instanceof HTMLSelectElement && target.dataset.choice) {
        this.choices.set(target.dataset.choice, target.value);
        this.render(target.dataset.focus);
      } else if (target instanceof HTMLInputElement && target.dataset.quantity) {
        this.selection.setQuantity(target.dataset.quantity, target.valueAsNumber);
        this.render(target.dataset.focus);
        this.announce(`${this.selection.totals().count} piezas en tu selecci\xF3n.`);
      }
    } catch (error) {
      this.announce(error instanceof Error ? error.message : "Revisa la cantidad.", true);
      this.render(target instanceof HTMLElement ? target.dataset.focus : "");
    }
  }
  async submit() {
    if (this.busy || this.locked || this.selection.issue()) return;
    this.busy = true;
    this.render();
    this.announce("Estamos agregando tu selecci\xF3n al carrito.");
    try {
      const attempt = crypto.randomUUID();
      const result2 = this.config.nativeProxyUrl ? await submitNativeSelection(this.selection, this.config.root, this.config.nativeProxyUrl, attempt) : await submitSelection(this.selection, this.config.root, attempt);
      this.locked = result2.status !== "rejected";
      this.announce(result2.message, result2.status !== "complete");
      if (result2.status === "complete") {
        this.navigateToCart();
        return;
      }
    } catch (error) {
      this.announce(error instanceof Error && error.name === "Error" ? error.message : "No pudimos revisar tu carrito. Int\xE9ntalo de nuevo.", true);
    } finally {
      this.busy = false;
      if (this.isConnected) this.render();
    }
  }
  navigateToCart() {
    window.location.assign(this.config.cartUrl);
  }
};
if (!customElements.get("mayoreo-combo-builder")) customElements.define("mayoreo-combo-builder", MayoreoComboBuilder);
export {
  MayoreoComboBuilder
};
